import { isExchangeType, MessagingTopology } from "@internal/open-catalog";
import { FetchMqStatsService } from "../../../../../../services/FetchMqStatsService";
import { buildDelayedEdge, buildInputSourceEdge, buildInputTargetEdge, buildOutputTargetEdge, buildParkingLotEdge, buildWaitQueueEdge, MessagingEdge } from "./messaging.flow.edge";
import { buildDelayedQueueNode, buildInputNode, buildInputSourceNode, buildInputTargetNode, buildOutputNode, buildOutputTargetNode, buildOutputTargetQueueNode, buildParkingLotNode, buildWaitQueueNode, MessagingNode } from "./messaging.flow.node";

export interface MessagingItems {
  nodes: MessagingNode[],
  edges: MessagingEdge[]
}

const parseInputNodes = (
  topology: MessagingTopology,
  refresh: boolean,
  statsService: FetchMqStatsService | undefined,
  focusMode: boolean,
  searchValue: string,
): MessagingItems => {

  const { vhost, input, parkingLot, delayedQueue } = topology ? topology : {vhost: undefined, input: undefined, parkingLot: undefined, delayedQueue: undefined};

  const nodes: MessagingNode[] = [];
  const edges: MessagingEdge[] = [];

  if (!input || !vhost) {
    return { nodes, edges };
  }

  nodes.push(buildInputNode(vhost, input, refresh, statsService));

  if (delayedQueue) {
    nodes.push(buildDelayedQueueNode(vhost, delayedQueue, refresh, statsService));
    edges.push(buildDelayedEdge(delayedQueue.name, input.name));
  }

  if (parkingLot) {
    nodes.push(buildParkingLotNode(vhost, parkingLot, refresh, statsService, focusMode));
  }

  input.sources
    .filter(target => {
      if (!searchValue || searchValue === '') return true;
      if (!isExchangeType(target.type)) return true;
      return target.name.toLowerCase().includes(searchValue.toLowerCase());
    })
    .forEach(source => {
      nodes.push(buildInputSourceNode(vhost, source, refresh, statsService));
      edges.push(buildInputSourceEdge(source.name, input.name));
    });

  for (const target of input.targets) {
    nodes.push(buildInputTargetNode(vhost, target, refresh, statsService));
    edges.push(buildInputTargetEdge(input.name, target.name));
    if (target.wait) {
      nodes.push(buildWaitQueueNode(vhost, target.wait, refresh, statsService, focusMode));
      edges.push(buildWaitQueueEdge(target.name, target.wait.name));
    }
    if (parkingLot) {
      edges.push(buildParkingLotEdge(target.name, parkingLot.name));
    }
  }

  return { nodes, edges };
}

const parseOutputNodes = (
  topology: MessagingTopology,
  refresh: boolean,
  statsService: FetchMqStatsService | undefined,
  focusMode: boolean,
  searchValue: string,
): MessagingItems => {

  const { vhost, input, output } = topology || {};

  const nodes: MessagingNode[] = [];
  const edges: MessagingEdge[] = [];

  if (!output) {
    return { nodes, edges };
  }

  nodes.push(buildOutputNode(vhost, output, refresh, statsService));

  output.targets.filter(target => target.name !== input?.name)
    .filter(target => {
      if (!searchValue || searchValue === '') return true;
      if (!isExchangeType(target.type)) return true;
      return target.name.toLowerCase().includes(searchValue.toLowerCase());
    })
    .forEach(target => {
      if (isExchangeType(target.type)) {
        nodes.push(buildOutputTargetNode(vhost, target, refresh, statsService));
      } else {
        if (target.name.endsWith('.overseer')) {
          nodes.push(buildOutputTargetQueueNode(vhost, target, refresh, statsService, focusMode));
        } else {
          nodes.push(buildOutputTargetQueueNode(vhost, target, refresh, statsService));
        }
      }
      edges.push(buildOutputTargetEdge(target.name, output.name));
    });

  return { nodes, edges };
}

export const parseTopology = (
  topology: MessagingTopology,
  refresh: boolean,
  statsService: FetchMqStatsService | undefined,
  focusMode: boolean,
  searchValue: string,
): MessagingItems => {

  const { nodes: inputNodes, edges: inputEdges } = parseInputNodes(
    topology, refresh, statsService, focusMode, searchValue
  );

  const { nodes: outputNodes, edges: outputEdges } = parseOutputNodes(
    topology, refresh, statsService, focusMode, searchValue
  );

  return { nodes: [...inputNodes, ...outputNodes], edges: [...inputEdges, ...outputEdges] };
}

export const parseDraftTopology = (
  topology: MessagingTopology,
  focusMode: boolean,
  searchValue: string,
): MessagingItems => {
  return parseTopology(topology, false, undefined, focusMode, searchValue);
}